import { Link } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Snowflake, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { Button, Card, ConfirmDeleteDialog, DateTimeInput, Field, Input, inputCls, Spinner, StatusBadge } from "../../components/ui-kit";
import BlastChillingProcessFields, { BlastChillingConformityNote } from "../../components/BlastChillingProcessFields";
import { blastDate } from "../../components/BlastTrace";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";

const localDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
};

const PROCESS_LABELS = { rapid_cooling: "Raffreddamento rapido", blast_freezing: "Congelamento per conservazione" };

const now = new Date();
const currentMonth = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  .toISOString()
  .slice(0, 7);

function EditRecordModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState({
    process_type: record.process_type, protocol: record.protocol || "company_haccp",
    started_at: localDateTime(record.started_at), completed_at: localDateTime(record.completed_at),
    initial_core_temp: record.initial_core_temp, final_core_temp: record.final_core_temp,
    target_temp: record.target_temp, max_duration_minutes: record.max_duration_minutes || "",
    required_duration_minutes: record.required_duration_minutes || "", entire_mass_confirmed: record.entire_mass_confirmed || false,
    corrective_action: record.corrective_action || "", note: record.note || "",
    department_id: record.department_id || "", equipment_id: record.equipment_id || "",
  });
  const [saving, setSaving] = useState(false);

  const setProcess = (processType) => {
    if (processType === "rapid_cooling") setForm({ ...form, process_type: processType, protocol: "company_haccp", entire_mass_confirmed: false });
    else if (processType === "blast_freezing") setForm({ ...form, process_type: processType, protocol: "company_haccp", target_temp: -18, entire_mass_confirmed: false });
    else setForm({ ...form, process_type: processType, protocol: "eu_-20_24h", target_temp: -20, required_duration_minutes: "1440" });
  };
  const setProtocol = (protocol) => setForm({ ...form, protocol, target_temp: protocol === "eu_-35_15h" ? -35 : -20, required_duration_minutes: String(protocol === "eu_-35_15h" ? 900 : 1440) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        initial_core_temp: Number(form.initial_core_temp), final_core_temp: Number(form.final_core_temp),
        target_temp: Number(form.target_temp),
        max_duration_minutes: form.max_duration_minutes ? Number(form.max_duration_minutes) : null,
        required_duration_minutes: form.required_duration_minutes ? Number(form.required_duration_minutes) : null,
        started_at: new Date(form.started_at).toISOString(), completed_at: new Date(form.completed_at).toISOString(),
        department_id: form.department_id || null, equipment_id: form.equipment_id || null,
      };
      await api.patch(`/app/blast-chilling/${record.id}`, payload);
      toast.success("Abbattimento aggiornato");
      onSaved();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-navy/60 p-4 backdrop-blur-sm overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="mobile-safe-form relative mt-6 w-full max-w-2xl p-4 sm:p-8">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="h-5 w-5" /></button>
        <h2 className="pr-10 font-heading text-xl font-semibold text-navy">Modifica abbattimento — {record.product_name}</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BlastChillingProcessFields form={form} setForm={setForm} setProcess={setProcess} setProtocol={setProtocol} />
          <Field label="Inizio processo *"><DateTimeInput value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value })} aria-label="Inizio processo" /></Field>
          <Field label="Fine processo *"><DateTimeInput value={form.completed_at} onChange={(e) => setForm({ ...form, completed_at: e.target.value })} aria-label="Fine processo" /></Field>
          <Field label="Temperatura iniziale al cuore *"><Input type="number" step="0.1" value={form.initial_core_temp} onChange={(e) => setForm({ ...form, initial_core_temp: e.target.value })} /></Field>
          <Field label="Temperatura finale al cuore *"><Input type="number" step="0.1" value={form.final_core_temp} onChange={(e) => setForm({ ...form, final_core_temp: e.target.value })} /></Field>
        </div>
        <BlastChillingConformityNote form={form} setForm={setForm} />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Azione correttiva (obbligatoria se non conforme)"><textarea className={inputCls} rows={3} value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value.toUpperCase() })} /></Field>
          <Field label="Note"><textarea className={inputCls} rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value.toUpperCase() })} /></Field>
        </div>
        <div className="mt-5 flex gap-3">
          <Button variant="ghost" className="flex-1" onClick={onClose}>Annulla</Button>
          <Button variant="success" className="flex-1" loading={saving} onClick={save}>Salva modifiche</Button>
        </div>
      </Card>
    </div>
  );
}

export default function ProdottiAbbattuti() {
  const [month, setMonth] = useState(currentMonth);
  const [records, setRecords] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const load = useCallback(() => {
    setRecords(null);
    api.get(`/app/blast-chilling?month=${month}`)
      .then(({ data }) => setRecords(data.records || []))
      .catch((error) => { setRecords([]); toast.error(formatApiErrorDetail(error.response?.data?.detail)); });
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const openReport = () => openServerPdf("/app/reports/blast-chilling", { month });

  // Raggruppa gli abbattimenti per produzione: sullo stesso lotto ce ne
  // possono essere al massimo 2 (raffreddamento + eventuale congelamento) e
  // vanno letti insieme, non come righe scollegate in un elenco unico.
  const groups = useMemo(() => {
    const map = new Map();
    for (const record of records || []) {
      if (!map.has(record.production_id)) map.set(record.production_id, []);
      map.get(record.production_id).push(record);
    }
    const withSortedItems = [...map.values()].map((items) =>
      [...items].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")));
    // Gruppi ordinati mettendo per primo il lotto abbattuto più di recente.
    return withSortedItems.sort((a, b) => {
      const lastA = a[a.length - 1].created_at || "";
      const lastB = b[b.length - 1].created_at || "";
      return lastB.localeCompare(lastA);
    });
  }, [records]);

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/app/blast-chilling/${deleting.id}`);
      toast.success("Abbattimento eliminato");
      setDeleting(null);
      load();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setDeleteBusy(false); }
  };

  return <div className="space-y-6" data-testid="blast-chilling-list-page">
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Registro HACCP</p>
      <div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold text-navy">Prodotti abbattuti (lista)</h1><AcademyTitleBadge /></div>
      <p className="mt-1 text-sm text-slate-500">Consulta lo storico mensile degli abbattimenti e scarica il registro in PDF.</p>
    </div>
    <Card className="p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Field label="Mese"><Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} data-testid="blast-list-month" /></Field>
        <Button variant="outline" className="w-full sm:w-auto" onClick={openReport} data-testid="blast-list-report"><Download className="h-4 w-4" /> Report PDF</Button>
      </div>
    </Card>
    {records === null ? <Spinner /> : records.length === 0
      ? <Card className="p-8 text-center text-slate-400"><Snowflake className="mx-auto mb-3 h-9 w-9 opacity-50" />Nessun prodotto abbattuto nel mese selezionato.</Card>
      : <div className="space-y-4">{groups.map((group) => {
          const head = group[0];
          return (
            <Card key={head.production_id} className="overflow-hidden p-0" data-testid={`blast-group-${head.production_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <h2 className="font-heading font-semibold text-navy truncate">{head.product_name}</h2>
                  <p className="text-xs text-slate-500">Lotto {head.production_lot || "—"} · {head.site_name}{head.department_name ? ` · ${head.department_name}` : ""}</p>
                </div>
                <span className="shrink-0 rounded-full bg-navy/10 px-2.5 py-1 text-xs font-semibold text-navy">
                  {group.length} abbattiment{group.length === 1 ? "o" : "i"} {group.length >= 2 && "/ 2 max"}
                </span>
              </div>
              <div className="divide-y divide-hairline">
                {group.map((record, idx) => {
                  const isLast = idx === group.length - 1;
                  return (
                    <div key={record.id}
                        className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center ${isLast ? "bg-emerald-50/50" : "bg-white"}`}
                        data-testid={`blast-record-${record.id}`}>
                      <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold ${isLast ? "bg-navy text-gold-bright" : "bg-slate-200 text-slate-500"}`}>
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-sm font-semibold ${isLast ? "text-navy" : "text-slate-500"}`}>
                            {PROCESS_LABELS[record.process_type] || "Antiparassitario ittico"}
                          </span>
                          <StatusBadge status={record.status === "conforme" ? "verde" : "rosso"}>
                            {record.status === "conforme" ? "Conforme" : "Non conforme"}
                          </StatusBadge>
                          {isLast ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                              <CheckCircle2 className="h-3 w-3" /> Ultimo — usa questo per l'etichetta
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                              Superato dal successivo
                            </span>
                          )}
                        </div>
                        <p className={`mt-1 text-sm ${isLast ? "text-slate-600" : "text-slate-400"}`}>
                          Da {record.initial_core_temp} °C a {record.final_core_temp} °C in {record.duration_minutes} minuti ·
                          {" "}{blastDate(record.completed_at)} · {record.operator_name}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
                        <Link className="text-sm font-semibold text-sky-800 underline" to={`/app/etichette-abbattuti?record=${encodeURIComponent(record.id)}`}>Etichetta QR</Link>
                        <button type="button" onClick={() => setEditing(record)} className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:underline" data-testid={`blast-edit-${record.id}`}><Pencil className="h-3.5 w-3.5" /> Modifica</button>
                        <button type="button" onClick={() => setDeleting(record)} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-testid={`blast-delete-${record.id}`}><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}</div>}

    {editing && <EditRecordModal record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    <ConfirmDeleteDialog
      open={!!deleting} busy={deleteBusy}
      title="Eliminare questo abbattimento?"
      description={deleting ? `L'abbattimento di "${deleting.product_name}" (lotto ${deleting.production_lot || "—"}) verrà rimosso definitivamente, insieme alla valutazione di shelf life calcolata automaticamente per questo abbattimento. Questa operazione non può essere annullata.` : ""}
      onCancel={() => setDeleting(null)} onConfirm={confirmDelete}
    />
  </div>;
}
